// import external libraries
import { useQuery } from 'react-query';
import dayjs from 'dayjs';

// import hooks
import { requests } from 'RestAPIs/rapini';

// import utils
import { entityFiltersForView, toCategoryFilter } from './Backlog.utils';
import { extractPlanningEntity } from './components/BacklogGrid/BacklogGrid.utils';

// import types
import type { QueryClient } from 'react-query';
import type {
  BackLogResponse,
  BackLogResponseList,
  PlanningEntityType
} from 'generated/index';
import type { BacklogEntityFilter, BacklogTaskCategory } from './Backlog.types';

type UseGetBacklogParams = {
  startDate: Date;
  endDate: Date;
  includeAllSummaries: boolean;
  entityFilters: BacklogEntityFilter[];
  /** The plan units the takenlijst is scoped to, as cost-centre ids. */
  organizationalUnitIds: number[];
  /** The tiles the planner has on — the backend detects only these. */
  activeCategories: BacklogTaskCategory[];
  /** Which planning entity's rows to report on — one type at a time. */
  rowEntityType: PlanningEntityType;
  /**
   * True while the page has nothing worth asking yet: a view still missing the scope it cannot work
   * without — a plan unit or a named person, see missingRequiredFilter — or plan unit picks that have not
   * resolved to their cost centres yet. Decided by the page rather than here, so what it shows and what it
   * withholds cannot disagree.
   */
  isRequestHeld: boolean;
};

/** The root of every backlog query key, and what a targeted refresh matches its cached queries by. */
const BACKLOG_QUERY_KEY = 'backlog';

/**
 * Everything a backlog request is scoped by — and, as `[BACKLOG_QUERY_KEY, scope]`, the query key itself.
 *
 * One named object rather than a flat list of key elements on purpose: `refreshBacklogForEntities` has to
 * read a cached query's scope back to repeat its request, and with a positional key every field added here
 * has to be mirrored in that destructure — miss one (as `organizationalUnitCostCenterIds` was) and every field after
 * it silently shifts by one, so the row type is read out of the category slot. Read by name, that cannot
 * happen.
 *
 * The list fields are sorted so the key only changes when the filter *contents* change, not on every array
 * identity — react-query hashes the object with a stable, key-sorted stringify.
 */
type BacklogQueryScope = {
  start: string;
  end: string;
  includeAllSummaries: boolean;
  /** The view's own entity picks as `{type}:{id}`, sorted — see entityFiltersForView. */
  entityKey: string;
  /** The picked plan units as the cost centres they stand for, sorted. */
  organizationalUnitCostCenterIds: number[];
  /**
   * The tiles that were on, sorted. Part of the scope, or switching a tile would be served the response
   * fetched for the previous selection — rows for a category that is now off.
   */
  categories: BacklogTaskCategory[];
  /** The planning entity type whose rows the query reports on — one type at a time. */
  rowEntityType: PlanningEntityType;
};

/**
 * Reads the backlog timeline for the care receivers or employees the filters name.
 *
 * `getBacklogTimeline` is a POST — rapini therefore generates it as a mutation — but it is
 * semantically a read, so it is wrapped in `useQuery` here.
 *
 * Either `organizationalUnitIds` or `planningEntities` has to name something; a request that narrows by
 * neither resolves to nobody server-side and comes back empty. The page holds it rather than sending it,
 * so the planner sees which field to fill instead of a grid that is empty for a reason nothing states.
 *
 * The two narrow rather than widen each other: a plan unit scopes to the care receivers whose treatments
 * are recorded under it, and a named patient is reported only if they are also under it.
 *
 * `includeAllSummaries: false` asks the backend for deviating weeks only — shortages and surpluses
 * alike — and it then also leaves out rules and care receivers that have nothing to report.
 */
export const useGetBacklog = ({
  startDate,
  endDate,
  includeAllSummaries,
  entityFilters,
  organizationalUnitIds,
  activeCategories,
  rowEntityType,
  isRequestHeld
}: UseGetBacklogParams) => {
  const start = dayjs(startDate).format('YYYY-MM-DD');
  const end = dayjs(endDate).format('YYYY-MM-DD');

  // Only the active view's picks narrow the list — see entityFiltersForView.
  const viewEntityFilters = entityFiltersForView(entityFilters, rowEntityType);

  // Sorted, so the scope — and with it the query key — only changes when the filter contents actually
  // change, not on every array identity.
  const scope: BacklogQueryScope = {
    start,
    end,
    includeAllSummaries,
    entityKey: viewEntityFilters
      .map((f) => `${f.planningEntityType}:${f.entityId}`)
      .sort()
      .join(','),
    organizationalUnitCostCenterIds: [...organizationalUnitIds].sort(
      (a, b) => a - b
    ),
    categories: [...activeCategories].sort(),
    rowEntityType
  };

  // No onError snackbar: Backlog.tsx already renders an inline error state with a retry button for
  // isError, which is discoverable and actionable where a snackbar just disappears.
  return useQuery<BackLogResponseList>(
    [BACKLOG_QUERY_KEY, scope],
    () =>
      requests.getBacklogTimeline({
        period: { start, end },
        includeAllSummaries,
        planningEntities: viewEntityFilters.map((f) => ({
          entityId: f.entityId,
          planningEntityType: f.planningEntityType
        })),
        // A plan unit is a cost centre. It travels in its own request field rather than as a planning
        // entity: the backend reads it as the scope to resolve rows from, where a planning entity
        // narrows within that scope.
        organizationalUnitIds: scope.organizationalUnitCostCenterIds,
        categories: toCategoryFilter(activeCategories),
        planningEntityType: rowEntityType
      }),
    {
      refetchOnWindowFocus: false,
      // Nothing selected means nothing to show, so there is nothing worth asking for — and a view that is
      // not scoped yet has not been asked a question worth sending.
      enabled: activeCategories.length > 0 && !isRequestHeld
    }
  );
};

/** A backlog row's entity id, derived the same way the grid rows are keyed. */
const backlogEntityId = (row: BackLogResponse): string =>
  extractPlanningEntity(
    row.planningEntity,
    (row.planningEntityType ?? 'CLIENT') as PlanningEntityType
  ).id;

/**
 * Merge a targeted backlog result into the cached list: refresh only the affected entities and keep every
 * other cached row exactly as the full backlog returned it.
 *
 * The fetch is trusted **only for the affected entities**. The backend scopes a targeted request to the
 * requested entities of the requested row type (see `BacklogEntityRowsHelper.reportOnRows`, which drops a
 * finding on an entity outside the request's scope), so it should return just those — but taking only the
 * affected entities out of the result is kept as a safety net:
 * were an unscoped result to also carry an incomplete, overlap-only row for some other overlapping entity,
 * splicing it in would duplicate that entity or overwrite its full cached row with a stripped-down one. So
 * the affected entities are replaced with their freshly fetched rows and all other cached rows are left
 * untouched. An affected entity the fetch no longer returns (nothing left to report — e.g. an unassigned
 * care receiver whose overlap is gone) is dropped.
 */
export const mergeBacklogEntities = (
  cached: BackLogResponseList,
  fetched: BackLogResponseList,
  affectedEntityIds: string[]
): BackLogResponseList => {
  const affected = new Set(affectedEntityIds);
  const refreshed = fetched.filter((row) => affected.has(backlogEntityId(row)));
  const kept = cached.filter((row) => !affected.has(backlogEntityId(row)));
  return [...kept, ...refreshed];
};

/**
 * Refresh the backlog for only the given entities instead of invalidating the whole list (which refetches
 * every care receiver). Fetches those entities' rows and merges them into each *mounted* backlog query. A
 * delete passes the appointment's own participants; an update passes the entities the update reports as
 * affected. No entities → nothing to do.
 *
 * The caller hands over a mixed bag — the appointment's care receivers, employees, resources and its
 * workplace — because it cannot know which list is on screen. Each cached query narrows that bag itself:
 * the ids are sent as entities of *that* query's row type, so a query showing employee rows asks about the
 * employees and simply matches nothing for the rest.
 */
export const refreshBacklogForEntities = async (
  queryClient: QueryClient,
  entityIds: string[]
): Promise<void> => {
  const uniqueIds = Array.from(new Set(entityIds.filter(Boolean)));
  if (!uniqueIds.length) {
    return;
  }

  // Mounted queries only: getQueriesData also hands back inactive cache entries from periods the planner
  // navigated away from, and each of those would cost a full backlog request of its own while nobody is
  // looking at its result.
  const cachedQueries = queryClient.getQueriesData<BackLogResponseList>({
    queryKey: BACKLOG_QUERY_KEY,
    active: true
  });

  await Promise.all(
    cachedQueries.map(async ([queryKey, cached]) => {
      if (!cached) {
        return;
      }
      const [, scope] = queryKey as [string, BacklogQueryScope];

      // A narrowed view must stay narrowed: refreshing an entity the planner filtered out would splice a
      // row into the list that the filter says does not belong there. The scope carries the view's picks
      // as `{type}:{id}`, so the affected entities are intersected with them.
      const viewEntityIds = scope.entityKey
        ? scope.entityKey.split(',').map((pick) => pick.split(':')[1])
        : null;
      const affectedIds = viewEntityIds
        ? uniqueIds.filter((id) => viewEntityIds.includes(id))
        : uniqueIds;
      if (!affectedIds.length) {
        return;
      }

      const fetched = await requests.getBacklogTimeline({
        period: { start: scope.start, end: scope.end },
        includeAllSummaries: scope.includeAllSummaries,
        // Typed as this query's own row type: that is the only type it reports rows for, so it is the
        // only type these ids can narrow.
        planningEntities: affectedIds.map((entityId) => ({
          entityId,
          planningEntityType: scope.rowEntityType
        })),
        // The rest of the request is what this cached query was fetched with, so the rows that come back
        // are comparable to the ones being replaced. The plan units in particular: they are the scope the
        // rows were resolved from, and a refresh that dropped them would ask a wider question whose extra
        // rows the merge would splice in.
        organizationalUnitIds: scope.organizationalUnitCostCenterIds,
        categories: toCategoryFilter(scope.categories),
        planningEntityType: scope.rowEntityType
      });
      queryClient.setQueryData<BackLogResponseList>(
        queryKey,
        mergeBacklogEntities(cached, fetched, affectedIds)
      );
    })
  );
};
